/**
 * npm-registry.ts — npm registry version lookups shared by `ss upgrade`
 * (extracted from src/commands/upgrade.ts) and `ss doctor` (the "CLI Update"
 * diagnostic: is a newer @atlashub/smartstack-cli published?).
 *
 * Offline-tolerant by contract: every failure path returns null (with an
 * optional warning log) — callers degrade gracefully instead of hanging or
 * throwing. A 10s abort guards `ss doctor` against a wedged network.
 */
import { logger } from './logger.js';

const FETCH_TIMEOUT_MS = 10_000;

/**
 * Fetches the latest version of an npm package using dist-tags.
 * Uses 'next' tag for prerelease, 'latest' tag for stable.
 * Falls back to version list scan if dist-tags are unavailable.
 * `quiet` suppresses the warning logs (doctor --json must stay clean).
 */
export async function getLatestNpmVersion(
  packageName: string,
  prerelease: boolean,
  options: { quiet?: boolean } = {},
): Promise<string | null> {
  const warn = (msg: string) => {
    if (!options.quiet) logger.warning(msg);
  };
  const ctrl = new AbortController();
  const timer = setTimeout(() => ctrl.abort(), FETCH_TIMEOUT_MS);
  try {
    const response = await fetch(`https://registry.npmjs.org/${packageName.replace('/', '%2F')}`, {
      signal: ctrl.signal,
    });

    if (!response.ok) {
      warn(`Could not fetch versions for ${packageName} from npm`);
      return null;
    }

    const data = await response.json() as {
      versions: Record<string, unknown>;
      'dist-tags'?: Record<string, string>;
    };

    // Prefer dist-tags for accurate version resolution
    const distTags = data['dist-tags'];
    if (distTags) {
      const tag = prerelease ? 'next' : 'latest';
      if (distTags[tag]) {
        return distTags[tag];
      }
      // Fallback to 'latest' if 'next' not available
      if (prerelease && distTags['latest']) {
        return distTags['latest'];
      }
    }

    // Fallback: scan version list
    const versions = Object.keys(data.versions);
    if (!versions || versions.length === 0) {
      return null;
    }

    if (prerelease) {
      const prereleaseVersions = versions.filter(v => v.includes('-'));
      if (prereleaseVersions.length > 0) {
        return prereleaseVersions[prereleaseVersions.length - 1];
      }
    }

    const stableVersions = versions.filter(v => !v.includes('-'));
    if (stableVersions.length > 0) {
      return stableVersions[stableVersions.length - 1];
    }

    return versions[versions.length - 1];
  } catch (error) {
    warn(`Error fetching latest npm version for ${packageName}: ${error instanceof Error ? error.message : error}`);
    return null;
  } finally {
    clearTimeout(timer);
  }
}

/** True when `candidate` is a strictly newer x.y.z than `installed`. Unknown /
 *  unparsable versions never claim newer (no update nag on bad data). */
export function isNewerVersion(candidate: string | null, installed: string | null): boolean {
  if (!candidate || !installed) return false;
  const parse = (v: string): number[] | null => {
    const m = /^(\d+)\.(\d+)\.(\d+)/.exec(v.trim());
    return m ? [Number(m[1]), Number(m[2]), Number(m[3])] : null;
  };
  const c = parse(candidate);
  const i = parse(installed);
  if (!c || !i) return false;
  for (let k = 0; k < 3; k++) {
    if (c[k] !== i[k]) return c[k] > i[k];
  }
  return false;
}
